import {
  INSTAGRAM_FEED_RATIO_RANGE,
  OutputPresetId,
  getOutputPreset,
} from '@/content/instagram'
import {
  GRID_TILE_RATIO,
  NodeKind,
  SplitKind,
  documentOutputRatio,
  nativeSize,
  outputContainLayout,
  presetIdForSplit,
  validateDocument,
  type Document,
  type CompositionNode,
  type SourceId,
  type SplitSettings,
} from '@/core/document'
import { ResizeMode, type ExportOptions, type ExportOutputSpec, type ExportPlan } from '@/core/export/types'
import { ExportTooLargeError } from '@/core/export/errors'
import { validateExportOptions } from '@/core/export/validate'
import type { AspectRatio, Rect, Size } from '@/core/geometry'

interface PlannedOutputs {
  renderSize: Size
  outputs: ExportOutputSpec[]
  readme?: ExportPlan['readme']
}

function singlePlan(document: Document, renderSize: Size): PlannedOutputs {
  const preset = getOutputPreset(document.output.presetId)
  const suffix = preset.id === OutputPresetId.Original
    ? 'original'
    : preset.ratioLabel.replace(':', 'x')
  return {
    renderSize,
    outputs: [{
      sourceRect: { x: 0, y: 0, ...renderSize },
      targetSize: { ...renderSize },
      fileNameSuffix: `-${suffix}`,
    }],
  }
}

function carouselOriginalPlan(renderSize: Size, split: Extract<SplitSettings, { kind: SplitKind.Carousel }>): PlannedOutputs {
  const segmentWidth = Math.floor(renderSize.width / split.tiles)
  const tileWidthFromHeight = Math.floor(renderSize.height * split.tileRatio.w / split.tileRatio.h)
  const tileWidth = Math.min(segmentWidth, tileWidthFromHeight)
  const tileHeight = Math.min(renderSize.height, Math.round(tileWidth * split.tileRatio.h / split.tileRatio.w))
  if (tileWidth <= 0 || tileHeight <= 0) throw new Error('Carousel split does not have enough pixels for every tile')
  const startX = Math.floor((renderSize.width - tileWidth * split.tiles) / 2)
  const startY = Math.floor((renderSize.height - tileHeight) / 2)
  return {
    renderSize,
    outputs: Array.from({ length: split.tiles }, (_, index) => ({
      sourceRect: { x: startX + index * tileWidth, y: startY, width: tileWidth, height: tileHeight },
      targetSize: { width: tileWidth, height: tileHeight },
      fileNameSuffix: `-slide${index + 1}`,
    })),
  }
}

function carouselScaledPlan(
  split: Extract<SplitSettings, { kind: SplitKind.Carousel }>,
  target: Size,
): PlannedOutputs {
  const renderSize = { width: target.width * split.tiles, height: target.height }
  return {
    renderSize,
    outputs: Array.from({ length: split.tiles }, (_, index) => ({
      sourceRect: { x: index * target.width, y: 0, width: target.width, height: target.height },
      targetSize: { ...target },
      fileNameSuffix: `-slide${index + 1}`,
    })),
  }
}

function gridReadme(outputs: readonly ExportOutputSpec[]): NonNullable<ExportPlan['readme']> {
  return {
    fileName: 'README.txt',
    contents: [
      'Instagram grid upload order',
      'Upload these files in the numbered order. Instagram shows the newest post first.',
      '',
      ...outputs.map((output, index) => `${index + 1}. ${output.fileNameSuffix}`),
    ].join('\n'),
  }
}

function gridOutputs(
  rows: number,
  tileSize: Size,
  postSourceSize: Size,
  targetSize: Size,
): ExportOutputSpec[] {
  const count = rows * 3
  return Array.from({ length: count }, (_, uploadIndex) => {
    const displayIndex = count - 1 - uploadIndex
    const row = Math.floor(displayIndex / 3)
    const column = displayIndex % 3
    const tileX = column * tileSize.width
    const tileY = row * tileSize.height
    const sourceRect: Rect = {
      x: tileX - (postSourceSize.width - tileSize.width) / 2,
      y: tileY - (postSourceSize.height - tileSize.height) / 2,
      width: postSourceSize.width,
      height: postSourceSize.height,
    }
    return {
      sourceRect,
      targetSize: { ...targetSize },
      fileNameSuffix: `-upload${uploadIndex + 1}-of-${count}`,
    }
  })
}

function gridOriginalPlan(renderSize: Size, split: Extract<SplitSettings, { kind: SplitKind.Grid }>): PlannedOutputs {
  const tileSize = {
    width: renderSize.width / 3,
    height: renderSize.height / split.rows,
  }
  const postSourceSize = {
    width: Math.round(tileSize.height * 4 / 5),
    height: Math.round(tileSize.height),
  }
  if (postSourceSize.width <= 0 || postSourceSize.height <= 0) {
    throw new Error('Grid split does not have enough pixels for every post')
  }
  const outputs = gridOutputs(split.rows, tileSize, postSourceSize, postSourceSize)
  return { renderSize, outputs, readme: gridReadme(outputs) }
}

function gridScaledPlan(
  split: Extract<SplitSettings, { kind: SplitKind.Grid }>,
  targetSize: Size,
): PlannedOutputs {
  const tileWidth = targetSize.width
  const tileHeight = tileWidth * GRID_TILE_RATIO.h / GRID_TILE_RATIO.w
  const tileSize = { width: tileWidth, height: tileHeight }
  const renderSize = { width: tileWidth * 3, height: tileHeight * split.rows }
  const postSourceSize = { width: tileHeight * 4 / 5, height: tileHeight }
  const outputs = gridOutputs(split.rows, tileSize, postSourceSize, targetSize)
  return { renderSize, outputs, readme: gridReadme(outputs) }
}

function splitOriginalPlan(renderSize: Size, split: SplitSettings): PlannedOutputs {
  switch (split.kind) {
    case SplitKind.Carousel:
      return carouselOriginalPlan(renderSize, split)
    case SplitKind.Grid:
      return gridOriginalPlan(renderSize, split)
  }
}

function splitScaledPlan(split: SplitSettings, targetSize: Size): PlannedOutputs {
  switch (split.kind) {
    case SplitKind.Carousel:
      return carouselScaledPlan(split, targetSize)
    case SplitKind.Grid:
      return gridScaledPlan(split, targetSize)
  }
}

function sizeForLongestSide(ratio: AspectRatio, longestSide: number): Size {
  if (ratio.w >= ratio.h) {
    return { width: longestSide, height: Math.max(1, Math.round(longestSide * ratio.h / ratio.w)) }
  }
  return { width: Math.max(1, Math.round(longestSide * ratio.w / ratio.h)), height: longestSide }
}

function instagramSingleTarget(document: Document, sources: ReadonlyMap<SourceId, Size>): Size {
  const preset = getOutputPreset(document.output.presetId)
  if (preset.targetSize) return { ...preset.targetSize }
  const ratio = documentOutputRatio(document, sources)
  return { width: 1080, height: Math.max(1, Math.round(1080 * ratio.h / ratio.w)) }
}

function scaledSplitTarget(split: SplitSettings, resizeMode: ResizeMode, customLongestSide: number | null): Size {
  if (resizeMode === ResizeMode.InstagramTarget) {
    return { ...getOutputPreset(presetIdForSplit(split)).targetSize }
  }
  if (resizeMode !== ResizeMode.Custom || customLongestSide === null) {
    throw new Error('Scaled split export requires InstagramTarget or a configured Custom size')
  }
  const ratio = split.kind === SplitKind.Carousel ? split.tileRatio : { w: 4, h: 5 }
  return sizeForLongestSide(ratio, customLongestSide)
}

function upscalingWarning(native: PlannedOutputs, target: PlannedOutputs): string[] | undefined {
  const nativeOutput = native.outputs[0]
  const targetOutput = target.outputs[0]
  if (!nativeOutput || !targetOutput) throw new Error('Export planning did not produce an output')
  return nativeOutput.targetSize.width < targetOutput.targetSize.width
    || nativeOutput.targetSize.height < targetOutput.targetSize.height
    ? [`Upscaled from ${nativeOutput.targetSize.width}×${nativeOutput.targetSize.height}`]
    : undefined
}

function emptyCollageCellCount(node: CompositionNode): number {
  switch (node.kind) {
    case NodeKind.Image:
      return 0
    case NodeKind.Frame:
      return emptyCollageCellCount(node.child)
    case NodeKind.Collage:
      return node.cells.reduce((count, child) => (
        count + (child ? emptyCollageCellCount(child) : 1)
      ), 0)
    default:
      throw new Error(`Cannot inspect unknown node kind "${String((node as { kind: unknown }).kind)}" for empty collage cells`)
  }
}

function placeholderWarnings(document: Document): string[] {
  const count = emptyCollageCellCount(document.root)
  if (count === 0) return []
  return [`${count} ${count === 1 ? 'placeholder is' : 'placeholders are'} empty`]
}

function originalRatioWarnings(document: Document, sources: ReadonlyMap<SourceId, Size>): string[] {
  if (document.output.presetId !== OutputPresetId.Original || document.output.split) return []
  const ratio = documentOutputRatio(document, sources)
  const value = ratio.w / ratio.h
  const tallest = INSTAGRAM_FEED_RATIO_RANGE.tallest.w / INSTAGRAM_FEED_RATIO_RANGE.tallest.h
  const widest = INSTAGRAM_FEED_RATIO_RANGE.widest.w / INSTAGRAM_FEED_RATIO_RANGE.widest.h
  if (value < tallest) return ['Instagram will crop this to 4:5 when uploaded to the feed']
  if (value > widest) return ['Instagram will crop this to 1.91:1 when uploaded to the feed']
  return []
}

function withWarnings(plan: PlannedOutputs, warnings: readonly string[]): ExportPlan {
  return warnings.length > 0 ? { ...plan, warnings: [...warnings] } : plan
}

function canvasArea(size: Size): number {
  return size.width * size.height
}

export function largestExportCanvasArea(plan: ExportPlan): number {
  if (plan.outputs.length === 0) throw new Error('Export plan must contain at least one output')
  return Math.max(canvasArea(plan.renderSize), ...plan.outputs.map((output) => canvasArea(output.targetSize)))
}

export function assertExportPlanWithinCanvasLimit(plan: ExportPlan, maxCanvasArea: number): void {
  if (!Number.isInteger(maxCanvasArea) || maxCanvasArea <= 0) {
    throw new Error('Export canvas limit must be a positive integer')
  }
  const neededArea = largestExportCanvasArea(plan)
  if (neededArea > maxCanvasArea) {
    throw new ExportTooLargeError(neededArea / 1_000_000, maxCanvasArea / 1_000_000)
  }
}

function withCanvasLimit(plan: ExportPlan, maxCanvasArea?: number): ExportPlan {
  if (maxCanvasArea !== undefined) assertExportPlanWithinCanvasLimit(plan, maxCanvasArea)
  return plan
}

export function planExport(
  document: Document,
  sources: ReadonlyMap<SourceId, Size>,
  options: ExportOptions,
  maxCanvasArea?: number,
): ExportPlan {
  validateExportOptions(options)
  validateDocument(document, sources)
  const nativeRootSize = nativeSize(document, sources, document.root.id)
  const resolvedRatio = documentOutputRatio(document, sources)
  const nativeRenderSize = outputContainLayout(nativeRootSize, resolvedRatio).size
  const split = document.output.split
  const nativePlan = split
    ? splitOriginalPlan(nativeRenderSize, split)
    : singlePlan(document, nativeRenderSize)
  const baseWarnings = [...placeholderWarnings(document), ...originalRatioWarnings(document, sources)]
  if (options.resizeMode === ResizeMode.Original) {
    return withCanvasLimit(withWarnings(nativePlan, baseWarnings), maxCanvasArea)
  }

  let targetPlan: PlannedOutputs
  if (split) {
    targetPlan = splitScaledPlan(split, scaledSplitTarget(split, options.resizeMode, options.customLongestSide))
  } else if (options.resizeMode === ResizeMode.InstagramTarget) {
    targetPlan = singlePlan(document, instagramSingleTarget(document, sources))
  } else {
    if (options.customLongestSide === null) throw new Error('Custom export requires a configured longest side')
    targetPlan = singlePlan(document, sizeForLongestSide(resolvedRatio, options.customLongestSide))
  }
  const warnings = [...baseWarnings, ...(upscalingWarning(nativePlan, targetPlan) ?? [])]
  return withCanvasLimit(withWarnings(targetPlan, warnings), maxCanvasArea)
}
